import { COLOR_THEME_KEYS, CUSTOMIZE_COLOR_THEME_KEYS, colorPalette } from '@theme';
import { convertHexToRGBA, getContrastTheme } from './usefulFuncs';
import { clearItemFromStorage, getItemFromStorage, setItemToStorage, STORAGE_KEYS } from './storage';
import { APP_COLOR_THEME_NAMES, DEFAULT_COLOR_THEME } from '@theme/appThemes';

const { charcoal, oak, whiteBlueTint } = colorPalette.primary;
const {
  white,
  darkCloud,
  lightCloud,
  cloud,
  darkBlue,
  orange,
  purple,
  lightCharcoal,
  green,
  red,
  darkRed,
  darkGreen,
  brightGreen,
  yellow,
  lightBlue,
} = colorPalette.secondary;

export const defaultCustomColorTheme = {
  header: '#2F2E2B',
  appBackground: '#211e18',
  component: '#2F2E2B',
  componentNoOpacity: '#2F2E2B',
  disabled: 'rgba(255,255,255,0.05)',
  overlay: 'rgba(247,244,242,0.6)',
  border: 'rgba(255,255,255,0.2)',
  lowContrastBorder: 'rgba(255,255,255,0.05)',
  text: '#ffffff',
  secondaryText: 'rgba(255,255,255,0.6)',
  buttonText: white,
  proColor: darkGreen,
  conColor: darkRed,
  neutral: lightCharcoal,
  negative: darkRed,
  positive: darkGreen,
  inputBg: 'transparent',
  avatarBackgroundColor: '#ab9419',
  buttonColor: '#ab9419',
  primaryCtaButton: '#ab9419',
  primaryEyeCatchingCtaButton: '#ab9419',
  badge: '#ab9419',
  error: red,
  warning: orange,
  hoverComponentBackground: 'rgba(171,148,25,0.1)',
};

export const colorThemeNameDisplayName = {
  [COLOR_THEME_KEYS.appBackground]: 'App Background',
  [COLOR_THEME_KEYS.componentNoOpacity]: 'Page Background',
  [COLOR_THEME_KEYS.text]: 'Text Color',
  [COLOR_THEME_KEYS.primaryCtaButton]: 'Button Color',
  [COLOR_THEME_KEYS.buttonText]: 'Button Text Color',
  [COLOR_THEME_KEYS.positive]: 'Positive Number',
  [COLOR_THEME_KEYS.neutral]: 'Neutral/ No Change',
  [COLOR_THEME_KEYS.negative]: 'Negative',
  [COLOR_THEME_KEYS.proColor]: 'Pro Color',
  [COLOR_THEME_KEYS.conColor]: 'Con Color',
};

export const selectDarkTheme = (theme) => {
  return theme.themes['dark-professional'];
};
export const selectLightTheme = (theme) => {
  return theme.themes['light-professional'];
};

export const COLOR_THEME_NAME_STORAGE_KEY = 'ct';

/*
  The saved name wins. The USING_CUSTOM_THEME flag used to override it, so a browser that had ever
  enabled a custom theme booted into `custom` forever -- the header's light/dark toggle wrote the
  name and left the flag alone, and the flag won on the next refresh. The flag is now only
  consulted when no name was ever saved, which is how storage written before persistColorThemeName
  existed still resolves.
*/
export const getStartingColorThemeName = () => {
  const savedName = window.localStorage.getItem(COLOR_THEME_NAME_STORAGE_KEY);
  if (Object.values(APP_COLOR_THEME_NAMES).includes(savedName)) return savedName;

  try {
    if (getItemFromStorage(STORAGE_KEYS.USING_CUSTOM_THEME) === 'true') {
      return APP_COLOR_THEME_NAMES.custom;
    }
  } catch (e) {
    setItemToStorage(STORAGE_KEYS.USING_CUSTOM_THEME, 'false');
  }

  return DEFAULT_COLOR_THEME;
};

/*
  Every theme change goes through here: the startup path reads two keys, and writing one without
  the other is what made a refresh forget the choice. Callers that switch to a saved custom theme
  pass its name so the palette comes back too, not just the mode.
*/
export const persistColorThemeName = (colorThemeName, { customThemeName } = {}) => {
  const isCustom = colorThemeName === APP_COLOR_THEME_NAMES.custom;

  setItemToStorage(COLOR_THEME_NAME_STORAGE_KEY, colorThemeName);
  setItemToStorage(STORAGE_KEYS.USING_CUSTOM_THEME, isCustom ? 'true' : 'false');

  if (isCustom && customThemeName) {
    setItemToStorage(STORAGE_KEYS.PREVIOUS_CUSTOM_THEME, customThemeName);
  } else {
    // Removed rather than set to null: setItemToStorage JSON-stringifies non-strings, so null was
    // being stored as the string "null" -- truthy, and never matching a saved theme name.
    clearItemFromStorage(STORAGE_KEYS.PREVIOUS_CUSTOM_THEME);
  }
};

export const getStartingColorTheme = () => {
  let startingColorTheme = defaultCustomColorTheme;

  try {
    const usingCustomTheme = getItemFromStorage(STORAGE_KEYS.USING_CUSTOM_THEME) === 'true';
    if (usingCustomTheme) {
      const prevCustomTheme = getItemFromStorage(STORAGE_KEYS.PREVIOUS_CUSTOM_THEME);
      if (prevCustomTheme) {
        const userThemes = _getThemesFromStorage();
        const userThemeNames = Object.keys(userThemes);
        if (userThemeNames.includes(prevCustomTheme)) {
          startingColorTheme = userThemes[prevCustomTheme];
        }
      }
    }
  } catch (e) {
    console.error(e);
  }

  return startingColorTheme;
};

export const generateComputedColors = (colors) => {
  const computedColors = {};

  if (colors[COLOR_THEME_KEYS.componentNoOpacity]) {
    // computedColors[COLOR_THEME_KEYS.component] = convertHexToRGBA(colors[COLOR_THEME_KEYS.componentNoOpacity], 0.9);
    computedColors[COLOR_THEME_KEYS.component] = colors[COLOR_THEME_KEYS.componentNoOpacity];
    computedColors[COLOR_THEME_KEYS.header] = colors[COLOR_THEME_KEYS.componentNoOpacity];
    computedColors[COLOR_THEME_KEYS.overlay] = convertHexToRGBA(
      getContrastTheme(colors[COLOR_THEME_KEYS.componentNoOpacity], {
        dark: oak,
        light: charcoal,
      }),
      0.6
    );
  }

  if (colors[COLOR_THEME_KEYS.primaryCtaButton]) {
    computedColors[COLOR_THEME_KEYS.hoverComponentBackground] = convertHexToRGBA(
      colors[COLOR_THEME_KEYS.primaryCtaButton],
      0.1
    );
    computedColors[COLOR_THEME_KEYS.avatarBackgroundColor] = colors[COLOR_THEME_KEYS.primaryCtaButton];
    computedColors[COLOR_THEME_KEYS.buttonColor] = colors[COLOR_THEME_KEYS.primaryCtaButton];
    computedColors[COLOR_THEME_KEYS.primaryEyeCatchingCtaButton] = colors[COLOR_THEME_KEYS.primaryCtaButton];
    computedColors[COLOR_THEME_KEYS.badge] = colors[COLOR_THEME_KEYS.primaryCtaButton];
  }

  if (colors[COLOR_THEME_KEYS.text]) {
    computedColors[COLOR_THEME_KEYS.border] = convertHexToRGBA(colors[COLOR_THEME_KEYS.text], 0.2);
    computedColors[COLOR_THEME_KEYS.lowContrastBorder] = convertHexToRGBA(colors[COLOR_THEME_KEYS.text], 0.05);
    computedColors[COLOR_THEME_KEYS.disabled] = convertHexToRGBA(colors[COLOR_THEME_KEYS.text], 0.05);
    computedColors[COLOR_THEME_KEYS.secondaryText] = convertHexToRGBA(colors[COLOR_THEME_KEYS.text], 0.6);
  }

  return computedColors;
};

export const saveColorTheme = (colorThemeName, customTheme) => {
  let storedColorTheme = window.localStorage.getItem('colorThemes');
  if (!storedColorTheme) {
    storedColorTheme = {};
  } else {
    storedColorTheme = JSON.parse(storedColorTheme);
  }
  storedColorTheme[colorThemeName] = customTheme;
  window.localStorage.setItem('colorThemes', JSON.stringify(storedColorTheme));
};

export const _getThemesFromStorage = () => {
  let storedColorThemes = window.localStorage.getItem('colorThemes');
  if (storedColorThemes) {
    return JSON.parse(storedColorThemes);
  } else {
    return {};
  }
};

export const FAVORITES_MAX = 24;

export const slugifyThemeName = (name) => {
  if (!name || !name.trim()) return 'untitled-theme';
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

export const exportThemeAsJSON = (name, colors) => {
  const payload = {
    $schema: 'tornado-color-theme/v1',
    name,
    exportedAt: new Date().toISOString(),
    colors,
  };
  return {
    json: JSON.stringify(payload, null, 2),
    filename: `${slugifyThemeName(name)}.tornado-theme.json`,
  };
};

export const downloadJSON = (content, filename) => {
  const blob = new Blob([content], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const parseThemeImport = (text, fallbackName) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Not valid JSON.' };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: 'File must contain a JSON object.' };
  }

  const colors = parsed.colors;
  if (typeof colors !== 'object' || colors === null || Array.isArray(colors)) {
    return { ok: false, error: 'Missing required "colors" object.' };
  }

  for (const key of CUSTOMIZE_COLOR_THEME_KEYS) {
    if (!(key in colors)) {
      return { ok: false, error: `Missing required color key: ${key}` };
    }
    if (typeof colors[key] !== 'string') {
      return { ok: false, error: `Color value for "${key}" must be a string.` };
    }
  }

  const rawName = typeof parsed.name === 'string' ? parsed.name.trim() : '';
  const name = rawName || fallbackName || 'Imported Theme';

  return { ok: true, name, colors };
};

export const getFavoritesFromStorage = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.COLOR_THEME_FAVORITES);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveFavoritesToStorage = (arr) => {
  setItemToStorage(STORAGE_KEYS.COLOR_THEME_FAVORITES, arr);
};

export const addFavoriteColor = (hex) => {
  const current = getFavoritesFromStorage();
  if (current.includes(hex)) return current;
  const next = [...current, hex];
  const capped = next.length > FAVORITES_MAX ? next.slice(next.length - FAVORITES_MAX) : next;
  saveFavoritesToStorage(capped);
  return capped;
};

export const removeFavoriteColor = (hex) => {
  const current = getFavoritesFromStorage();
  const next = current.filter((c) => c !== hex);
  saveFavoritesToStorage(next);
  return next;
};
